import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import {
  serializeManagementTimelineQuery,
  type ActivityDeletionBatchView,
  type DeletionItemOutcome,
  type RevalidatePhaseConformityResult,
} from '@internal/spassu-code-contract';
import {
  AnswerInteractionResult,
  Activity,
  ActivityFilterOptions,
  ActivityFilters,
  Approach,
  Artifact,
  AzureProject,
  AzureBranch,
  AzureRepository,
  ChatMessage,
  CreateActivityInput,
  ExecutionState,
  InteractionState,
  PaginatedResult,
  PipelineDefinition,
  PhaseMetrics,
  Phase,
  Question,
  StreamEvent,
  TaskItem,
  WorkerStatus,
  WorkerToken,
  SkillSummary,
  SkillEffectivenessEntry,
  DryRunResult,
  TimelineEvent,
  TimelineQueryOptions,
  SkillInvocationProjectionPage,
  ActivityManagementSummaryPage,
  ManagementTimelinePage,
  ManagementTimelineQueryOptions,
  TraceArtifactContentPage,
  TraceArtifactCollection,
  TraceAuditTrailPage,
  TraceDoraActivityPage,
  TraceParticipantsPage,
  CostSummaryEntry,
  DORAMetrics,
  ComplianceFramework,
  ComplianceReport,
  PaginatedEvents,
  AccessibleProject,
  ProjectMember,
  AuditLogEntry,
  AuditLogFilters,
  ComplianceMetrics,
  Role,
  AzureWorkItemType,
  AzureWorkItemDetail,
  SearchWorkItemsFilters,
  PaginatedWorkItems,
  Issue,
  IssueQuery,
  IssueScope,
  IssueSearchResult,
  ManagedProject,
  ProviderId,
  Repository,
  RepositoryBranch,
  RepositoryOwner,
  RepositoryProject,
  ScmProviderDescriptor,
  ResumeStreamConflict,
  ResumeStreamConflictInteractionState,
  ResumeStreamConflictReason,
  ReleaseCommandResult,
} from './types';
import { processSSEStream } from './sse-parser';

/** Options for SSE-streaming methods (sendMessage, resumeActivityStream, etc.) */
export interface StreamOptions {
  /** Called when a `:heartbeat` SSE comment arrives (connection alive signal). */
  onHeartbeat?: () => void;
  /** Called with the latest seqId every time an SSE event with `id:` is received. */
  onSeqId?: (seqId: number) => void;
  /** Last event ID received — used for deterministic replay on reconnect. */
  lastEventId?: number;
  /** Optional skill ID to override the default phase skill (from Skill Vault). */
  skillId?: string;
}

export interface SpassuCodeApi {
  /** @deprecated Use `listScmProviders()` and `listScmRepositories()` going forward. */
  listProjects(): Promise<AzureProject[]>;
  /** @deprecated Use `listScmRepositories(provider, owner, project?)` going forward. */
  listRepositories(projectId: string): Promise<AzureRepository[]>;
  /** @deprecated Use `listScmBranches(provider, repo)` going forward. */
  listBranches(projectId: string, repoId: string): Promise<AzureBranch[]>;

  /**
   * Multi-provider SCM endpoints. The legacy `listProjects/listRepositories/
   * listBranches` methods continue to work (they call /azure/* and the
   * backend serves them as deprecation aliases) — these new methods talk
   * to /scm/* and /boards/* and accept any registered provider.
   */
  listScmProviders(): Promise<ScmProviderDescriptor[]>;
  listScmOwners(provider: ProviderId): Promise<RepositoryOwner[]>;
  /**
   * Agrega owners de todos os providers registrados em uma lista única.
   * Usado pela tela "Gestão de Projetos" — cada owner equivale a um
   * `project_key` no esquema RBAC agnóstico (`project_key = repoOwner`,
   * `repository_key = repoName`). Se um provider falhar (rede, auth), ele
   * é silenciosamente excluído do resultado para não bloquear a página.
   */
  listManagedProjects(): Promise<ManagedProject[]>;
  /**
   * Lista todos os repositórios visíveis sob um (provider, owner),
   * abstraindo a hierarquia: para providers com `hasProject = true` (Azure
   * DevOps), enumera os projetos do owner e agrega os repos de cada um;
   * para providers flat (GitHub etc.), chama diretamente listScmRepositories.
   * Usado pelo AddMemberDialog onde a UI pensa em (provider, owner) e não
   * deve precisar saber da existência de uma camada intermediária.
   */
  listAllRepositoriesForOwner(
    provider: ProviderId,
    owner: string,
  ): Promise<Repository[]>;
  listScmProjects(
    provider: ProviderId,
    owner: string,
  ): Promise<RepositoryProject[]>;
  listScmRepositories(
    provider: ProviderId,
    owner: string,
    project?: string,
  ): Promise<Repository[]>;
  listScmBranches(
    provider: ProviderId,
    repo: Pick<Repository, 'externalId' | 'owner' | 'project' | 'name'>,
  ): Promise<RepositoryBranch[]>;
  searchIssues(
    provider: ProviderId | 'jira',
    query: IssueQuery,
  ): Promise<IssueSearchResult>;
  getIssue(
    provider: ProviderId | 'jira',
    externalId: string,
    scope: IssueScope,
  ): Promise<Issue>;
  createActivity(input: CreateActivityInput): Promise<Activity>;
  updateDescription(activityId: string, description: string): Promise<Activity>;
  listActivities(filters?: ActivityFilters): Promise<PaginatedResult<Activity>>;
  getActivityFilterOptions(
    filters?: Omit<ActivityFilters, 'createdBy' | 'page' | 'limit'>,
  ): Promise<ActivityFilterOptions>;
  /**
   * Global-admin administrative deletion (ADR-005). Replaces the retired
   * owner-wide `deleteIncompleteActivities`: the batch is explicit, per-item and
   * asynchronous, and it never deletes a completed activity.
   */
  requestActivityDeletionBatch(
    activityIds: string[],
    idempotencyKey: string,
  ): Promise<ActivityDeletionBatchView>;
  getActivityDeletionBatch(
    batchId: string,
  ): Promise<ActivityDeletionBatchView | null>;
  /** Separate, explicitly confirmed action for an agent that never returned. */
  overrideActivityDeletionCleanup(
    activityId: string,
    idempotencyKey: string,
  ): Promise<{ activityId: string; outcome: DeletionItemOutcome }>;
  releaseCommand(activityId: string): Promise<ReleaseCommandResult>;
  getPipelineDefinition(): Promise<PipelineDefinition>;
  getActivity(id: string): Promise<Activity>;
  sendMessage(
    activityId: string,
    message: string,
    onEvent: (event: StreamEvent) => void,
    signal?: AbortSignal,
    streamOptions?: StreamOptions,
  ): Promise<void>;
  resumeActivityStream(
    activityId: string,
    onEvent: (event: StreamEvent) => void,
    signal?: AbortSignal,
    streamOptions?: StreamOptions,
  ): Promise<void>;
  answerQuestion(
    activityId: string,
    answers: Record<string, string>,
  ): Promise<AnswerInteractionResult>;
  approvePhase(activityId: string): Promise<Activity>;
  revalidatePhaseConformity(
    activityId: string,
    phaseAttemptId: string,
  ): Promise<RevalidatePhaseConformityResult>;
  restartPhase(activityId: string, phase: string): Promise<void>;
  retryClone(activityId: string): Promise<{
    cloneStatus: 'success';
    clonePath: string | null;
    recovered: boolean;
  }>;
  getMetrics(activityId: string): Promise<PhaseMetrics[]>;
  getMessages(activityId: string, phase?: Phase): Promise<ChatMessage[]>;
  getArtifacts(activityId: string): Promise<Artifact[]>;
  uploadContextFile(activityId: string, file: File): Promise<Artifact>;
  getContextFiles(activityId: string): Promise<Artifact[]>;
  deleteContextFile(activityId: string, fileId: string): Promise<void>;
  savePrdDraft(activityId: string, content: string): Promise<ChatMessage>;
  saveTechSpecDraft(activityId: string, content: string): Promise<ChatMessage>;
  getTasks(
    activityId: string,
  ): Promise<{ tasks: TaskItem[]; executionState: ExecutionState }>;
  startExecution(
    activityId: string,
    onEvent: (event: StreamEvent) => void,
  ): Promise<void>;
  retryTask(
    activityId: string,
    taskNumber: string,
    onEvent: (event: StreamEvent) => void,
  ): Promise<void>;
  pauseExecution(activityId: string): Promise<void>;
  resumeExecution(
    activityId: string,
    onEvent: (event: StreamEvent) => void,
  ): Promise<void>;
  getWorkerStatus(): Promise<WorkerStatus>;
  getWorkerToken(): Promise<WorkerToken>;
  getPendingQuestion(
    activityId: string,
    phase: Phase,
  ): Promise<Question[] | null>;
  getPendingApproach(
    activityId: string,
    phase: Phase,
  ): Promise<{ question: string; approaches: Approach[] } | null>;
  getInteractionState(
    activityId: string,
    phase: Phase,
  ): Promise<InteractionState | null>;
  // Skill Vault
  listSkills(filters?: {
    status?: string;
    origin?: string;
    phase?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResult<SkillSummary>>;
  getSkill(id: string): Promise<SkillSummary>;
  createSkill(data: {
    name: string;
    content: string;
    phase?: string;
    description?: string;
  }): Promise<SkillSummary>;
  updateSkill(
    id: string,
    data: {
      name?: string;
      content?: string;
      phase?: string;
      description?: string;
    },
  ): Promise<SkillSummary>;
  signSkill(id: string): Promise<SkillSummary>;
  submitSkillForReview(id: string): Promise<SkillSummary>;
  approveSkill(id: string): Promise<SkillSummary>;
  submitCommunitySkill(data: {
    name: string;
    content: string;
    phase?: string;
    description?: string;
  }): Promise<SkillSummary>;
  getSkillEffectiveness(filters?: {
    phase?: string;
    limit?: number;
  }): Promise<SkillEffectivenessEntry[]>;
  // Dry-Run
  dryRun(activityId: string, message: string): Promise<DryRunResult>;
  // Available skills for chat autocomplete
  getAvailableSkills(activityId: string): Promise<{
    defaultSkill: string | null;
    skills: Array<{
      id: string;
      name: string;
      description: string | null;
      phase: string | null;
      isDefault: boolean;
    }>;
  }>;
  // CLI Activity Page
  getTimeline(
    activityId: string,
    filters?: TimelineQueryOptions,
  ): Promise<TimelineEvent[]>;
  getSkillInvocations(
    activityId: string,
  ): Promise<SkillInvocationProjectionPage>;
  getActivityManagementSummary(
    activityId: string,
  ): Promise<ActivityManagementSummaryPage | null>;
  getManagementTimeline(
    activityId: string,
    filters?: ManagementTimelineQueryOptions,
  ): Promise<ManagementTimelinePage>;
  getArtifactCollection(activityId: string): Promise<TraceArtifactCollection>;
  getArtifactContent(
    activityId: string,
    artifactId: string,
    options?: { offset?: number; limit?: number },
  ): Promise<TraceArtifactContentPage | null>;
  getActivityParticipants(activityId: string): Promise<TraceParticipantsPage>;
  getActivityDoraTrace(
    activityId: string,
  ): Promise<TraceDoraActivityPage | null>;
  getActivityAuditTrail(
    activityId: string,
  ): Promise<TraceAuditTrailPage | null>;
  getCostSummary(filters?: {
    groupBy?: 'provider' | 'agent' | 'model' | 'day' | 'activity';
    activityId?: string;
    includeLowFidelity?: boolean;
  }): Promise<CostSummaryEntry[]>;
  getDORAMetrics(
    activityId?: string,
    options?: { includeLowFidelity?: boolean },
  ): Promise<DORAMetrics | null>;
  getComplianceReport(
    activityId: string,
    framework: ComplianceFramework,
  ): Promise<ComplianceReport | null>;
  getComplianceFrameworks(): Promise<
    Array<{ id: string; name: string; description: string }>
  >;
  getAgentEvents(
    agentId: string,
    filters?: { activityId?: string; page?: number; limit?: number },
  ): Promise<PaginatedEvents>;
  // Roles & Access Control
  getMyRoles(): Promise<{ data: AccessibleProject[]; userId: string }>;
  getProjectMembers(projectKey: string): Promise<ProjectMember[]>;
  getAssignableRoles(projectKey: string): Promise<Role[]>;
  addProjectMember(
    projectKey: string,
    entityRef: string,
    role: Role,
    expiresAt?: string,
    repositoryKeys?: string[],
  ): Promise<ProjectMember>;
  removeProjectMember(
    projectKey: string,
    entityRef: string,
    role: Role,
    repositoryKey?: string | null,
  ): Promise<void>;
  updateProjectMemberRole(
    projectKey: string,
    entityRef: string,
    oldRole: Role,
    newRole: Role,
  ): Promise<ProjectMember>;
  renewProjectMember(
    projectKey: string,
    entityRef: string,
    role: Role,
    newExpiresAt: string,
  ): Promise<ProjectMember>;
  getAuditLog(
    filters?: AuditLogFilters,
  ): Promise<PaginatedResult<AuditLogEntry>>;
  exportAuditLogCsv(filters?: AuditLogFilters): Promise<Blob>;
  invalidateRbacCache(userEntityRef?: string): Promise<{ invalidated: string }>;
  getProjectPolicies(projectKey: string): Promise<Record<string, unknown>>;
  setProjectPolicy(
    projectKey: string,
    policyKey: string,
    value: unknown,
  ): Promise<unknown>;
  getProjectCompliance(
    projectKey: string,
    days?: number,
  ): Promise<ComplianceMetrics>;
  finalizeActivity(activityId: string): Promise<Activity>;
  // Azure Boards — Work Items
  listWorkItemTypes(projectId: string): Promise<AzureWorkItemType[]>;
  searchWorkItems(
    projectId: string,
    filters?: Omit<SearchWorkItemsFilters, 'projectId'>,
  ): Promise<PaginatedWorkItems>;
  getWorkItem(
    projectId: string,
    workItemId: number,
  ): Promise<AzureWorkItemDetail>;
}

export const spassuCodeApiRef = createApiRef<SpassuCodeApi>({
  id: 'plugin.spassu-code.api',
});

const RESUME_STREAM_CONFLICT_REASONS: ResumeStreamConflictReason[] = [
  'no_inflight_stream',
  'interaction_mismatch',
  'worker_disconnected',
];

const INTERACTION_STATUSES = [
  'streaming',
  'awaiting_user',
  'expired',
  'completed',
  'canceled',
] as const;

const INTERACTION_RESUME_MODES = ['direct', 'summary_required'] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function emptyManagementTimeline(activityId: string): ManagementTimelinePage {
  return {
    activityId,
    generatedAt: new Date(0).toISOString(),
    total: 0,
    page: 1,
    pageSize: 20,
    hasMore: false,
    filters: {},
    groups: [],
    markers: [],
  };
}

function emptyArtifactCollection(activityId: string): TraceArtifactCollection {
  return {
    activityId,
    generatedAt: new Date(0).toISOString(),
    total: 0,
    artifacts: [],
    deliveryCards: [],
    qualitySummary: [],
    categories: [],
    emptyState: {
      status: 'missing',
      label: 'Artefatos indisponíveis',
      explanation: 'A coleção de artefatos não está disponível.',
    },
  };
}

function emptyActivityManagementSummary(
  activityId: string,
): ActivityManagementSummaryPage {
  return {
    activityId,
    generatedAt: new Date(0).toISOString(),
    workPath: 'control-plane',
    situation: {
      statusLabel: 'Indisponivel',
      currentStepLabel: 'Sem resumo gerencial',
      healthLabel: 'Dados indisponiveis',
      responsibleLabel: 'Responsavel nao observado',
      agentLabel: 'Agente nao observado',
      updatedAt: null,
      partialData: true,
    },
    nextAction: {
      kind: 'workflow',
      label: 'Consultar rastreabilidade',
      description: 'O resumo gerencial ainda nao esta disponivel.',
      evidenceLabel: null,
      evidenceEventIds: [],
      createsPortalAction: false,
    },
    telemetryWarning: {
      label: 'Resumo indisponivel',
      description: 'O endpoint de resumo gerencial nao respondeu.',
      lastEventAt: null,
      silentForMinutes: null,
    },
    sessionCoverage: {
      state: 'uncovered',
      label: 'Sem cobertura de skills nesta sessão',
      description:
        'O resumo gerencial não respondeu; a cobertura da sessão não pode ser confirmada.',
      telemetrySessionId: null,
      emitterHealth: 'missing',
      pairingHealth: 'not-observed',
      emitterVersion: null,
      lastSignalAt: null,
      lastIngestedAt: null,
      stoppedAt: null,
      silentForMinutes: null,
      gap: null,
      qualifiers: [],
    },
    journey: {
      observedWindow: { startsAt: null, endsAt: null },
      steps: [],
    },
    stepDetails: [],
    timelineGroups: [],
  };
}

function emptyParticipants(activityId: string): TraceParticipantsPage {
  return {
    activityId,
    generatedAt: new Date(0).toISOString(),
    total: 0,
    participants: [],
    collaboratorState: {
      status: 'unavailable',
      label: 'Sem participantes observados',
      explanation: 'A lista de participantes não está disponível.',
    },
  };
}

function emptyActivityDoraTrace(activityId: string): TraceDoraActivityPage {
  return {
    activityId,
    generatedAt: new Date(0).toISOString(),
    summaryState: {
      status: 'unavailable',
      label: 'DORA indisponível',
      explanation: 'Indicadores DORA estritos não estão disponíveis.',
    },
    indicators: [],
  };
}

export class ResumeActivityStreamError extends Error {
  readonly statusCode: number;

  readonly conflict?: ResumeStreamConflict;

  constructor(
    message: string,
    options: {
      statusCode: number;
      conflict?: ResumeStreamConflict;
    },
  ) {
    super(message);
    this.name = 'ResumeActivityStreamError';
    this.statusCode = options.statusCode;
    this.conflict = options.conflict;
  }
}

export function isResumeActivityStreamError(
  error: unknown,
): error is ResumeActivityStreamError {
  return error instanceof ResumeActivityStreamError;
}

export class SpassuCodeClient implements SpassuCodeApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async getBaseUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('spassu-code');
  }

  private async extractError(
    response: Response,
    fallback: string,
  ): Promise<string> {
    const body = await this.parseErrorBody(response);
    return this.extractErrorMessage(body, fallback, response.status);
  }

  private async parseErrorBody(
    response: Response,
  ): Promise<Record<string, unknown> | null> {
    try {
      const body = await response.json();
      return isRecord(body) ? body : null;
    } catch {
      return null;
    }
  }

  private async throwApiError(
    response: Response,
    fallback: string,
  ): Promise<never> {
    let code: string | undefined;
    let message = fallback;
    try {
      const body = await response.json();
      if (typeof body.code === 'string') code = body.code;
      if (typeof body.error === 'string') {
        message = body.error;
      } else if (body.error && typeof body.error.message === 'string') {
        message = body.error.message;
      } else if (typeof body.message === 'string') {
        message = body.message;
      }
    } catch {
      message = `${fallback} (HTTP ${response.status})`;
    }
    const err = new Error(message) as Error & {
      code?: string;
      status?: number;
    };
    err.status = response.status;
    if (code) err.code = code;
    throw err;
  }

  private extractErrorMessage(
    body: Record<string, unknown> | null,
    fallback: string,
    statusCode: number,
  ): string {
    if (!body) {
      return `${fallback} (HTTP ${statusCode})`;
    }

    const error = body.error;
    if (typeof error === 'string') return error;

    if (isRecord(error)) {
      if (typeof error.message === 'string') return error.message;
      if (typeof error.name === 'string') return error.name;
      return JSON.stringify(error);
    }

    if (typeof body.message === 'string') return body.message;
    return fallback;
  }

  private parseResumeStreamConflict(
    body: Record<string, unknown> | null,
    fallback: string,
    statusCode: number,
  ): ResumeStreamConflict | undefined {
    if (statusCode !== 409) {
      return undefined;
    }

    const reason = this.parseResumeStreamConflictReason(body?.reason);
    const retryable =
      typeof body?.retryable === 'boolean' ? body.retryable : undefined;
    const interactionState = this.parseResumeConflictInteractionState(
      body?.interactionState,
    );

    return {
      error: this.extractErrorMessage(body, fallback, statusCode),
      ...(reason ? { reason } : {}),
      ...(retryable !== undefined ? { retryable } : {}),
      ...(interactionState ? { interactionState } : {}),
    };
  }

  private parseResumeStreamConflictReason(
    value: unknown,
  ): ResumeStreamConflictReason | undefined {
    return typeof value === 'string' &&
      RESUME_STREAM_CONFLICT_REASONS.includes(
        value as ResumeStreamConflictReason,
      )
      ? (value as ResumeStreamConflictReason)
      : undefined;
  }

  private parseResumeConflictInteractionState(
    value: unknown,
  ): ResumeStreamConflict['interactionState'] | undefined {
    if (!isRecord(value)) {
      return undefined;
    }

    const { status, resumeMode, terminalReason } = value;
    if (
      !this.isInteractionStatus(status) ||
      !this.isInteractionResumeMode(resumeMode)
    ) {
      return undefined;
    }

    return {
      status,
      resumeMode,
      terminalReason:
        typeof terminalReason === 'string' || terminalReason === null
          ? terminalReason
          : null,
    };
  }

  private isInteractionStatus(
    value: unknown,
  ): value is ResumeStreamConflictInteractionState['status'] {
    return (
      typeof value === 'string' &&
      INTERACTION_STATUSES.includes(
        value as ResumeStreamConflictInteractionState['status'],
      )
    );
  }

  private isInteractionResumeMode(
    value: unknown,
  ): value is ResumeStreamConflictInteractionState['resumeMode'] {
    return (
      typeof value === 'string' &&
      INTERACTION_RESUME_MODES.includes(
        value as ResumeStreamConflictInteractionState['resumeMode'],
      )
    );
  }

  async listProjects(): Promise<AzureProject[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/azure/projects`);

    if (!response.ok) {
      throw new Error('Failed to list Azure DevOps projects');
    }

    return response.json();
  }

  // ─── Multi-provider SCM endpoints ────────────────────────────────────────

  async listScmProviders(): Promise<ScmProviderDescriptor[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/scm/providers`);
    if (!response.ok) throw new Error('Failed to list SCM providers');
    return response.json();
  }

  async listScmOwners(provider: ProviderId): Promise<RepositoryOwner[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/scm/${encodeURIComponent(provider)}/owners`,
    );
    if (!response.ok) throw new Error('Failed to list SCM owners');
    return response.json();
  }

  async listManagedProjects(): Promise<ManagedProject[]> {
    const providers = await this.listScmProviders();
    const ownersPerProvider = await Promise.all(
      providers.map(async p => {
        try {
          const owners = await this.listScmOwners(p.id);
          return owners.map<ManagedProject>(o => ({
            name: o.name,
            providerId: p.id,
            providerLabel: p.displayName,
          }));
        } catch {
          // Provider isolado não bloqueia a página: por exemplo, um provider
          // configurado mas com credenciais expiradas continua aparecendo no
          // dropdown de outros (Azure ainda funciona se o GitHub falhar).
          return [];
        }
      }),
    );
    return ownersPerProvider.flat();
  }

  async listAllRepositoriesForOwner(
    provider: ProviderId,
    owner: string,
  ): Promise<Repository[]> {
    const providers = await this.listScmProviders();
    const descriptor = providers.find(p => p.id === provider);
    if (!descriptor) {
      throw new Error(`Provider '${provider}' is not registered`);
    }

    if (!descriptor.hierarchy.hasProject) {
      // GitHub e similares: lista direta sob o owner.
      return await this.listScmRepositories(provider, owner);
    }

    // Azure DevOps e outros com camada de project: enumera os projects do
    // owner e agrega repos de todos. Falha em um project não interrompe os
    // demais — devolve só os que carregaram com sucesso.
    const projects = await this.listScmProjects(provider, owner);
    const reposPerProject = await Promise.all(
      projects.map(async project => {
        try {
          return await this.listScmRepositories(provider, owner, project.name);
        } catch {
          return [];
        }
      }),
    );
    return reposPerProject.flat();
  }

  async listScmProjects(
    provider: ProviderId,
    owner: string,
  ): Promise<RepositoryProject[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/scm/${encodeURIComponent(
        provider,
      )}/owners/${encodeURIComponent(owner)}/projects`,
    );
    if (!response.ok) throw new Error('Failed to list SCM projects');
    return response.json();
  }

  async listScmRepositories(
    provider: ProviderId,
    owner: string,
    project?: string,
  ): Promise<Repository[]> {
    const baseUrl = await this.getBaseUrl();
    const path = project
      ? `${baseUrl}/scm/${encodeURIComponent(
          provider,
        )}/owners/${encodeURIComponent(owner)}/projects/${encodeURIComponent(
          project,
        )}/repositories`
      : `${baseUrl}/scm/${encodeURIComponent(
          provider,
        )}/owners/${encodeURIComponent(owner)}/repositories`;
    const response = await this.fetchApi.fetch(path);
    if (!response.ok) throw new Error('Failed to list SCM repositories');
    return response.json();
  }

  async listScmBranches(
    provider: ProviderId,
    repo: {
      externalId: string;
      owner: string;
      project: string | null;
      name: string;
    },
  ): Promise<RepositoryBranch[]> {
    const baseUrl = await this.getBaseUrl();
    // Identificadores do repositório vão na query string, nunca no path: o
    // `externalId` do GitHub é `owner/repo` (contém `/`), que no path vira
    // `%2F` e é rejeitado com 404 pelo ingress de produção (Envoy). Na query,
    // o `URLSearchParams` codifica com segurança e o proxy não interfere.
    const params = new URLSearchParams({
      externalId: repo.externalId,
      owner: repo.owner,
      name: repo.name,
    });
    if (repo.project) params.set('project', repo.project);
    const response = await this.fetchApi.fetch(
      `${baseUrl}/scm/${encodeURIComponent(
        provider,
      )}/repositories/branches?${params.toString()}`,
    );
    if (!response.ok) throw new Error('Failed to list branches');
    return response.json();
  }

  async searchIssues(
    provider: ProviderId | 'jira',
    query: IssueQuery,
  ): Promise<IssueSearchResult> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams({ owner: query.scope.owner });
    if (query.scope.project) params.set('project', query.scope.project);
    if (query.scope.repo) params.set('repo', query.scope.repo);
    if (query.state) params.set('state', query.state);
    if (query.q) params.set('q', query.q);
    if (query.label) params.set('label', query.label);
    if (query.assignee) params.set('assignee', query.assignee);
    if (query.board) params.set('board', query.board);
    if (query.sprint) params.set('sprint', query.sprint);
    if (query.page) params.set('page', query.page);
    if (query.pageSize !== undefined)
      params.set('pageSize', String(query.pageSize));
    const response = await this.fetchApi.fetch(
      `${baseUrl}/boards/${encodeURIComponent(
        provider,
      )}/issues?${params.toString()}`,
    );
    if (!response.ok) throw new Error('Failed to search issues');
    return response.json();
  }

  async getIssue(
    provider: ProviderId | 'jira',
    externalId: string,
    scope: IssueScope,
  ): Promise<Issue> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams({ owner: scope.owner });
    if (scope.project) params.set('project', scope.project);
    if (scope.repo) params.set('repo', scope.repo);
    const response = await this.fetchApi.fetch(
      `${baseUrl}/boards/${encodeURIComponent(
        provider,
      )}/issues/${encodeURIComponent(externalId)}?${params.toString()}`,
    );
    if (!response.ok) throw new Error('Failed to fetch issue');
    return response.json();
  }

  async listRepositories(projectId: string): Promise<AzureRepository[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/azure/projects/${encodeURIComponent(projectId)}/repositories`,
    );

    if (!response.ok) {
      throw new Error('Failed to list repositories');
    }

    return response.json();
  }

  async listBranches(
    projectId: string,
    repoId: string,
  ): Promise<AzureBranch[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/azure/projects/${encodeURIComponent(
        projectId,
      )}/repositories/${encodeURIComponent(repoId)}/branches`,
    );

    if (!response.ok) {
      throw new Error('Failed to list branches');
    }

    return response.json();
  }

  async createActivity(input: CreateActivityInput): Promise<Activity> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/activities`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      throw new Error(
        await this.extractError(response, 'Failed to create activity'),
      );
    }

    return response.json();
  }

  async updateDescription(
    activityId: string,
    description: string,
  ): Promise<Activity> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/activities/${encodeURIComponent(activityId)}/description`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description }),
      },
    );

    if (!response.ok) {
      throw new Error(
        await this.extractError(response, 'Failed to update description'),
      );
    }

    return response.json();
  }

  async listActivities(
    filters: ActivityFilters = {},
  ): Promise<PaginatedResult<Activity>> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();

    if (filters.status) params.set('status', filters.status);
    if (filters.workPath) params.set('workPath', filters.workPath);
    if (filters.azureProject) params.set('azureProject', filters.azureProject);
    if (filters.azureRepository)
      params.set('azureRepository', filters.azureRepository);
    if (filters.source) params.set('source', filters.source);
    if (filters.issueExternalId)
      params.set('issueExternalId', filters.issueExternalId);
    if (filters.createdBy) params.set('createdBy', filters.createdBy);
    if (filters.createdFrom) params.set('createdFrom', filters.createdFrom);
    if (filters.createdToExclusive)
      params.set('createdToExclusive', filters.createdToExclusive);
    if (filters.page) params.set('page', String(filters.page));
    if (filters.limit) params.set('limit', String(filters.limit));
    if (filters.mineOnly) params.set('mineOnly', 'true');

    const query = params.toString();
    const url = query
      ? `${baseUrl}/activities?${query}`
      : `${baseUrl}/activities`;

    const response = await this.fetchApi.fetch(url);

    if (!response.ok) {
      throw new Error('Failed to list activities');
    }

    return response.json();
  }

  async getActivityFilterOptions(
    filters: Omit<ActivityFilters, 'createdBy' | 'page' | 'limit'> = {},
  ): Promise<ActivityFilterOptions> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();

    if (filters.status) params.set('status', filters.status);
    if (filters.workPath) params.set('workPath', filters.workPath);
    if (filters.azureProject) params.set('azureProject', filters.azureProject);
    if (filters.azureRepository)
      params.set('azureRepository', filters.azureRepository);
    if (filters.source) params.set('source', filters.source);
    if (filters.issueExternalId)
      params.set('issueExternalId', filters.issueExternalId);
    if (filters.createdFrom) params.set('createdFrom', filters.createdFrom);
    if (filters.createdToExclusive)
      params.set('createdToExclusive', filters.createdToExclusive);
    if (filters.mineOnly) params.set('mineOnly', 'true');

    const query = params.toString();
    const url = query
      ? `${baseUrl}/activities/filter-options?${query}`
      : `${baseUrl}/activities/filter-options`;

    const response = await this.fetchApi.fetch(url);

    if (!response.ok) {
      throw new Error('Failed to get activity filter options');
    }

    return response.json();
  }

  async requestActivityDeletionBatch(
    activityIds: string[],
    idempotencyKey: string,
  ): Promise<ActivityDeletionBatchView> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/control-plane/admin/activity-deletion-batches`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify({ activityIds }),
      },
    );
    if (!response.ok) {
      throw new Error(await deletionErrorMessage(response));
    }
    return response.json();
  }

  async getActivityDeletionBatch(
    batchId: string,
  ): Promise<ActivityDeletionBatchView | null> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/control-plane/admin/activity-deletion-batches/${encodeURIComponent(
        batchId,
      )}`,
      { cache: 'no-store' },
    );
    // A fully purged batch is removed, so 404 means "finished", not "error".
    if (response.status === 404) return null;
    if (!response.ok) {
      throw new Error(await deletionErrorMessage(response));
    }
    return response.json();
  }

  async overrideActivityDeletionCleanup(
    activityId: string,
    idempotencyKey: string,
  ): Promise<{ activityId: string; outcome: DeletionItemOutcome }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/control-plane/admin/activities/${encodeURIComponent(
        activityId,
      )}/deletion-override`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
        },
        body: '{}',
      },
    );
    if (!response.ok) {
      throw new Error(await deletionErrorMessage(response));
    }
    return response.json();
  }

  async releaseCommand(activityId: string): Promise<ReleaseCommandResult> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/activities/${activityId}/release-command`,
      { method: 'POST' },
    );

    if (!response.ok) {
      throw new Error(
        await this.extractError(response, 'Failed to release command'),
      );
    }

    return response.json();
  }

  async getPipelineDefinition(): Promise<PipelineDefinition> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/pipeline-definition`,
    );

    if (!response.ok) {
      throw new Error('Failed to get pipeline definition');
    }

    return response.json();
  }

  async getActivity(id: string): Promise<Activity> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/activities/${id}`);

    if (!response.ok) {
      await this.throwApiError(response, 'Failed to get activity');
    }

    return response.json();
  }

  async sendMessage(
    activityId: string,
    message: string,
    onEvent: (event: StreamEvent) => void,
    signal?: AbortSignal,
    streamOptions?: StreamOptions,
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/activities/${activityId}/messages`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          ...(streamOptions?.skillId ? { skillId: streamOptions.skillId } : {}),
        }),
        signal,
      },
    );

    if (!response.ok) {
      throw new Error(
        await this.extractError(response, 'Failed to send message'),
      );
    }
    await this.processSSEStream(response, onEvent, streamOptions);
  }

  async resumeActivityStream(
    activityId: string,
    onEvent: (event: StreamEvent) => void,
    signal?: AbortSignal,
    streamOptions?: StreamOptions,
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const lastEventId = streamOptions?.lastEventId;
    const url = lastEventId
      ? `${baseUrl}/activities/${activityId}/stream?lastEventId=${lastEventId}`
      : `${baseUrl}/activities/${activityId}/stream`;
    const response = await this.fetchApi.fetch(url, {
      method: 'GET',
      signal,
    });

    if (!response.ok) {
      const body = await this.parseErrorBody(response);
      const fallback = 'Failed to resume activity stream';
      throw new ResumeActivityStreamError(
        this.extractErrorMessage(body, fallback, response.status),
        {
          statusCode: response.status,
          conflict: this.parseResumeStreamConflict(
            body,
            fallback,
            response.status,
          ),
        },
      );
    }

    await this.processSSEStream(response, onEvent, streamOptions);
  }

  async answerQuestion(
    activityId: string,
    answers: Record<string, string>,
  ): Promise<AnswerInteractionResult> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/activities/${activityId}/answer-question`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers }),
      },
    );

    if (!response.ok) {
      throw new Error(
        await this.extractError(response, 'Failed to submit answer'),
      );
    }

    return response.json();
  }

  async approvePhase(activityId: string): Promise<Activity> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/activities/${activityId}/approve-phase`,
      { method: 'POST' },
    );

    if (!response.ok) {
      throw new Error(
        await this.extractError(response, 'Failed to approve phase'),
      );
    }

    return response.json();
  }

  async revalidatePhaseConformity(
    activityId: string,
    phaseAttemptId: string,
  ): Promise<RevalidatePhaseConformityResult> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/activities/${activityId}/phase-conformity/revalidate`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phaseAttemptId }),
      },
    );
    if (!response.ok) {
      throw new Error(
        await this.extractError(response, 'Falha ao revalidar arquivo'),
      );
    }
    return response.json();
  }

  async retryClone(activityId: string): Promise<{
    cloneStatus: 'success';
    clonePath: string | null;
    recovered: boolean;
  }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/activities/${activityId}/retry-clone`,
      { method: 'POST' },
    );

    if (!response.ok) {
      throw new Error(
        await this.extractError(response, 'Falha ao reclonar repositório'),
      );
    }

    return response.json();
  }

  async restartPhase(activityId: string, phase: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/activities/${activityId}/restart-phase`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phase }),
      },
    );

    if (!response.ok) {
      throw new Error(
        await this.extractError(response, 'Falha ao reiniciar fase'),
      );
    }
  }

  async getMetrics(activityId: string): Promise<PhaseMetrics[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/activities/${activityId}/metrics`,
    );

    if (!response.ok) {
      throw new Error('Failed to get metrics');
    }

    return response.json();
  }

  async getMessages(activityId: string, phase?: Phase): Promise<ChatMessage[]> {
    const baseUrl = await this.getBaseUrl();
    const url = phase
      ? `${baseUrl}/activities/${activityId}/messages?phase=${phase}`
      : `${baseUrl}/activities/${activityId}/messages`;

    const response = await this.fetchApi.fetch(url);

    if (!response.ok) {
      throw new Error('Failed to get messages');
    }

    return response.json();
  }

  async getArtifacts(activityId: string): Promise<Artifact[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/activities/${activityId}/artifacts`,
    );

    if (!response.ok) {
      throw new Error('Failed to get artifacts');
    }

    return response.json();
  }

  async uploadContextFile(activityId: string, file: File): Promise<Artifact> {
    const baseUrl = await this.getBaseUrl();
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.fetchApi.fetch(
      `${baseUrl}/activities/${activityId}/context-files`,
      { method: 'POST', body: formData },
    );

    if (!response.ok) {
      throw new Error(
        await this.extractError(response, 'Failed to upload context file'),
      );
    }

    return response.json();
  }

  async getContextFiles(activityId: string): Promise<Artifact[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/activities/${activityId}/context-files`,
    );

    if (!response.ok) {
      throw new Error('Failed to get context files');
    }

    return response.json();
  }

  async deleteContextFile(activityId: string, fileId: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/activities/${activityId}/context-files/${fileId}`,
      { method: 'DELETE' },
    );

    if (!response.ok) {
      throw new Error(
        await this.extractError(response, 'Failed to delete context file'),
      );
    }
  }

  getContextFileDownloadUrl(
    baseUrl: string,
    activityId: string,
    fileId: string,
  ): string {
    return `${baseUrl}/activities/${activityId}/context-files/${fileId}/download`;
  }

  async savePrdDraft(
    activityId: string,
    content: string,
  ): Promise<ChatMessage> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/activities/${activityId}/prd-draft`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      },
    );

    if (!response.ok) {
      throw new Error(
        await this.extractError(response, 'Failed to save PRD draft'),
      );
    }

    return response.json();
  }

  async saveTechSpecDraft(
    activityId: string,
    content: string,
  ): Promise<ChatMessage> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/activities/${activityId}/techspec-draft`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      },
    );

    if (!response.ok) {
      throw new Error(
        await this.extractError(response, 'Failed to save TechSpec draft'),
      );
    }

    return response.json();
  }

  async getTasks(
    activityId: string,
  ): Promise<{ tasks: TaskItem[]; executionState: ExecutionState }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/activities/${activityId}/tasks`,
    );

    if (!response.ok) {
      throw new Error('Failed to get tasks');
    }

    return response.json();
  }

  async startExecution(
    activityId: string,
    onEvent: (event: StreamEvent) => void,
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/activities/${activityId}/tasks/start`,
      { method: 'POST' },
    );

    if (!response.ok) {
      throw new Error(
        await this.extractError(response, 'Failed to start execution'),
      );
    }

    await this.processSSEStream(response, onEvent);
  }

  async retryTask(
    activityId: string,
    taskNumber: string,
    onEvent: (event: StreamEvent) => void,
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/activities/${activityId}/tasks/${encodeURIComponent(
        taskNumber,
      )}/retry`,
      { method: 'POST' },
    );

    if (!response.ok) {
      throw new Error(
        await this.extractError(response, 'Failed to retry task'),
      );
    }

    await this.processSSEStream(response, onEvent);
  }

  async pauseExecution(activityId: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/activities/${activityId}/tasks/pause`,
      { method: 'POST' },
    );

    if (!response.ok) {
      throw new Error(
        await this.extractError(response, 'Failed to pause execution'),
      );
    }
  }

  async resumeExecution(
    activityId: string,
    onEvent: (event: StreamEvent) => void,
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/activities/${activityId}/tasks/resume`,
      { method: 'POST' },
    );

    if (!response.ok) {
      throw new Error(
        await this.extractError(response, 'Failed to resume execution'),
      );
    }

    await this.processSSEStream(response, onEvent);
  }

  async getWorkerToken(): Promise<WorkerToken> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/worker-token`);

    if (!response.ok) {
      throw new Error(
        await this.extractError(response, 'Failed to generate worker token'),
      );
    }

    return response.json();
  }

  async getPendingQuestion(
    activityId: string,
    phase: Phase,
  ): Promise<Question[] | null> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/activities/${activityId}/pending-question?phase=${encodeURIComponent(
        phase,
      )}`,
    );

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      return null;
    }

    const body = await response.json();
    return body.questions ?? null;
  }

  async getPendingApproach(
    activityId: string,
    phase: Phase,
  ): Promise<{ question: string; approaches: Approach[] } | null> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/activities/${activityId}/pending-approach?phase=${encodeURIComponent(
        phase,
      )}`,
    );

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      return null;
    }

    const body = await response.json();
    return body.approaches ?? null;
  }

  async getInteractionState(
    activityId: string,
    phase: Phase,
  ): Promise<InteractionState | null> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/activities/${activityId}/interaction-state?phase=${encodeURIComponent(
        phase,
      )}`,
    );

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(
        await this.extractError(response, 'Failed to get interaction state'),
      );
    }

    return response.json();
  }

  /**
   * Devolver `{ connected: false }` para resposta não ok apagava a diferença
   * entre "não há worker" e "não foi possível saber": 401 de sessão expirada e
   * 500 de serviço fora viravam desconexão. A classificação agora acontece em
   * um único lugar (`hooks/useWorkerStatus`), e para isso este método precisa
   * ser honesto e falhar (ADR-004).
   */
  async getWorkerStatus(): Promise<WorkerStatus> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/worker-status`);

    if (!response.ok) {
      throw new Error(`Failed to get worker status (${response.status})`);
    }

    return response.json();
  }

  // ─── Skill Vault Methods ───

  async listSkills(
    filters: {
      status?: string;
      origin?: string;
      phase?: string;
      page?: number;
      limit?: number;
    } = {},
  ): Promise<PaginatedResult<SkillSummary>> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.origin) params.set('origin', filters.origin);
    if (filters.phase) params.set('phase', filters.phase);
    if (filters.page) params.set('page', String(filters.page));
    if (filters.limit) params.set('limit', String(filters.limit));
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.fetchApi.fetch(`${baseUrl}/agents/skills${qs}`);
    return response.json();
  }

  async getSkill(id: string): Promise<SkillSummary> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/agents/skills/${id}`,
    );
    return response.json();
  }

  async createSkill(data: {
    name: string;
    content: string;
    phase?: string;
    description?: string;
  }): Promise<SkillSummary> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/agents/skills`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return response.json();
  }

  async updateSkill(
    id: string,
    data: {
      name?: string;
      content?: string;
      phase?: string;
      description?: string;
    },
  ): Promise<SkillSummary> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/agents/skills/${id}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      },
    );
    return response.json();
  }

  async signSkill(id: string): Promise<SkillSummary> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/agents/skills/${id}/sign`,
      { method: 'POST' },
    );
    return response.json();
  }

  async submitSkillForReview(id: string): Promise<SkillSummary> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/agents/skills/${id}/submit-review`,
      { method: 'POST' },
    );
    return response.json();
  }

  async approveSkill(id: string): Promise<SkillSummary> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/agents/skills/${id}/approve`,
      { method: 'POST' },
    );
    return response.json();
  }

  async submitCommunitySkill(data: {
    name: string;
    content: string;
    phase?: string;
    description?: string;
  }): Promise<SkillSummary> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/agents/skills/community-submit`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      },
    );
    return response.json();
  }

  async getSkillEffectiveness(
    filters: { phase?: string; limit?: number } = {},
  ): Promise<SkillEffectivenessEntry[]> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();
    if (filters.phase) params.set('phase', filters.phase);
    if (filters.limit) params.set('limit', String(filters.limit));
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.fetchApi.fetch(
      `${baseUrl}/agents/skills/effectiveness${qs}`,
    );
    return response.json();
  }

  // ─── Dry-Run ───

  async dryRun(activityId: string, message: string): Promise<DryRunResult> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/activities/${activityId}/dry-run`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
      },
    );
    return response.json();
  }

  async getAvailableSkills(activityId: string): Promise<{
    defaultSkill: string | null;
    skills: Array<{
      id: string;
      name: string;
      description: string | null;
      phase: string | null;
      isDefault: boolean;
    }>;
  }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/activities/${activityId}/available-skills`,
    );
    return response.json();
  }

  // ─── CLI Activity Page Methods ───

  async getTimeline(
    activityId: string,
    filters: TimelineQueryOptions = {},
  ): Promise<TimelineEvent[]> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();
    if (filters.source) params.set('source', filters.source);
    if (filters.eventType) params.set('eventType', filters.eventType);
    if (filters.cursor) params.set('cursor', filters.cursor);
    if (typeof filters.limit === 'number') {
      params.set('limit', String(filters.limit));
    }
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.fetchApi.fetch(
      `${baseUrl}/agents/timeline/${activityId}${qs}`,
    );
    if (!response.ok) return [];
    const body = await response.json();
    // Backend returns { events: TimelineEvent[], nextCursor: string | null }
    if (Array.isArray(body)) return body;
    if (body && Array.isArray(body.events)) return body.events;
    return [];
  }

  async getSkillInvocations(
    activityId: string,
  ): Promise<SkillInvocationProjectionPage> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/agents/skill-invocations/${activityId}`,
    );
    if (!response.ok) {
      return {
        activityId,
        generatedAt: new Date(0).toISOString(),
        sourceEventCount: 0,
        invocations: [],
      };
    }
    return response.json();
  }

  async getManagementTimeline(
    activityId: string,
    filters: ManagementTimelineQueryOptions = {},
  ): Promise<ManagementTimelinePage> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams(
      serializeManagementTimelineQuery(filters),
    );
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.fetchApi.fetch(
      `${baseUrl}/agents/management-timeline/${activityId}${qs}`,
    );
    if (!response.ok) return emptyManagementTimeline(activityId);
    return response.json();
  }

  async getActivityManagementSummary(
    activityId: string,
  ): Promise<ActivityManagementSummaryPage | null> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/agents/management-summary/${activityId}`,
    );
    if (!response.ok) return emptyActivityManagementSummary(activityId);
    return response.json();
  }

  async getArtifactCollection(
    activityId: string,
  ): Promise<TraceArtifactCollection> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/agents/artifact-collection/${activityId}`,
    );
    if (!response.ok) return emptyArtifactCollection(activityId);
    return response.json();
  }

  async getArtifactContent(
    activityId: string,
    artifactId: string,
    options: { offset?: number; limit?: number } = {},
  ): Promise<TraceArtifactContentPage | null> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();
    if (typeof options.offset === 'number') {
      params.set('offset', String(options.offset));
    }
    if (typeof options.limit === 'number') {
      params.set('limit', String(options.limit));
    }
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.fetchApi.fetch(
      `${baseUrl}/agents/artifact-content/${activityId}/${artifactId}${qs}`,
    );
    if (!response.ok) return null;
    return response.json();
  }

  async getActivityParticipants(
    activityId: string,
  ): Promise<TraceParticipantsPage> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/agents/participants/${activityId}`,
    );
    if (!response.ok) return emptyParticipants(activityId);
    return response.json();
  }

  async getActivityDoraTrace(
    activityId: string,
  ): Promise<TraceDoraActivityPage | null> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/agents/activity-dora/${activityId}`,
    );
    if (!response.ok) return emptyActivityDoraTrace(activityId);
    return response.json();
  }

  async getActivityAuditTrail(
    activityId: string,
  ): Promise<TraceAuditTrailPage | null> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/agents/audit-trail/${activityId}`,
    );
    if (!response.ok) return null;
    return response.json();
  }

  async getCostSummary(
    filters: {
      groupBy?: 'provider' | 'agent' | 'model' | 'day' | 'activity';
      activityId?: string;
      includeLowFidelity?: boolean;
    } = {},
  ): Promise<CostSummaryEntry[]> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();
    if (filters.groupBy) params.set('groupBy', filters.groupBy);
    if (filters.activityId) params.set('activityId', filters.activityId);
    if (filters.includeLowFidelity) params.set('includeLowFidelity', 'true');
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.fetchApi.fetch(
      `${baseUrl}/agents/cost-summary${qs}`,
    );
    if (!response.ok) return [];
    return response.json();
  }

  async getDORAMetrics(
    activityId?: string,
    options: { includeLowFidelity?: boolean } = {},
  ): Promise<DORAMetrics | null> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();
    if (activityId) params.set('activityId', activityId);
    if (options.includeLowFidelity) params.set('includeLowFidelity', 'true');
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.fetchApi.fetch(
      `${baseUrl}/agents/dora-metrics${qs}`,
    );
    if (!response.ok) return null;
    return response.json();
  }

  async getComplianceReport(
    activityId: string,
    framework: ComplianceFramework,
  ): Promise<ComplianceReport | null> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/agents/compliance/${activityId}/${framework}`,
    );
    if (!response.ok) return null;
    return response.json();
  }

  async getComplianceFrameworks(): Promise<
    Array<{ id: string; name: string; description: string }>
  > {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/agents/compliance/frameworks`,
    );
    if (!response.ok) return [];
    return response.json();
  }

  async getAgentEvents(
    agentId: string,
    filters: { activityId?: string; page?: number; limit?: number } = {},
  ): Promise<PaginatedEvents> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();
    if (filters.activityId) params.set('activityId', filters.activityId);
    if (filters.page) params.set('page', String(filters.page));
    if (filters.limit) params.set('limit', String(filters.limit));
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.fetchApi.fetch(
      `${baseUrl}/agents/${agentId}/events${qs}`,
    );
    if (!response.ok) return { data: [], total: 0, page: 1, limit: 20 };
    return response.json();
  }

  // ─── RBAC Methods ───

  async getMyRoles(): Promise<{ data: AccessibleProject[]; userId: string }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/roles/my-roles`);
    if (!response.ok) return { data: [], userId: '' };
    return response.json();
  }

  async getProjectMembers(projectKey: string): Promise<ProjectMember[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/roles/projects/${encodeURIComponent(projectKey)}/members`,
    );
    if (!response.ok) return [];
    const result = await response.json();
    return result.data;
  }

  async getAssignableRoles(projectKey: string): Promise<Role[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/roles/projects/${encodeURIComponent(
        projectKey,
      )}/assignable-roles`,
    );
    if (!response.ok) return [];
    const result = await response.json();
    return result.data;
  }

  async addProjectMember(
    projectKey: string,
    entityRef: string,
    role: Role,
    expiresAt?: string,
    repositoryKeys?: string[],
  ): Promise<ProjectMember> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/roles/projects/${encodeURIComponent(projectKey)}/members`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entityRef, role, expiresAt, repositoryKeys }),
      },
    );
    if (!response.ok) {
      const msg = await this.extractError(response, 'Failed to add member');
      throw new Error(msg);
    }
    return response.json();
  }

  async removeProjectMember(
    projectKey: string,
    entityRef: string,
    role: Role,
    repositoryKey?: string | null,
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/roles/projects/${encodeURIComponent(projectKey)}/members`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityRef,
          role,
          repositoryKey: repositoryKey ?? null,
        }),
      },
    );
    if (!response.ok) {
      const msg = await this.extractError(response, 'Failed to remove member');
      throw new Error(msg);
    }
  }

  async updateProjectMemberRole(
    projectKey: string,
    entityRef: string,
    oldRole: Role,
    newRole: Role,
  ): Promise<ProjectMember> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/roles/projects/${encodeURIComponent(projectKey)}/members`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entityRef, oldRole, newRole }),
      },
    );
    if (!response.ok) {
      const msg = await this.extractError(response, 'Failed to update role');
      throw new Error(msg);
    }
    return response.json();
  }

  async getAuditLog(
    filters: AuditLogFilters = {},
  ): Promise<PaginatedResult<AuditLogEntry>> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();
    if (filters.projectKey) params.set('projectKey', filters.projectKey);
    if (filters.action) params.set('action', filters.action);
    if (filters.fromDate) params.set('fromDate', filters.fromDate);
    if (filters.toDate) params.set('toDate', filters.toDate);
    if (filters.search) params.set('search', filters.search);
    if (filters.page) params.set('page', String(filters.page));
    if (filters.limit) params.set('limit', String(filters.limit));
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.fetchApi.fetch(`${baseUrl}/roles/audit${qs}`);
    if (!response.ok) return { data: [], total: 0, page: 1, limit: 50 };
    return response.json();
  }

  async renewProjectMember(
    projectKey: string,
    entityRef: string,
    role: Role,
    newExpiresAt: string,
  ): Promise<ProjectMember> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/roles/projects/${encodeURIComponent(
        projectKey,
      )}/members/renew`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entityRef, role, newExpiresAt }),
      },
    );
    if (!response.ok) {
      const msg = await this.extractError(response, 'Failed to renew member');
      throw new Error(msg);
    }
    return response.json();
  }

  async exportAuditLogCsv(filters: AuditLogFilters = {}): Promise<Blob> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();
    if (filters.projectKey) params.set('projectKey', filters.projectKey);
    if (filters.action) params.set('action', filters.action);
    if (filters.fromDate) params.set('fromDate', filters.fromDate);
    if (filters.toDate) params.set('toDate', filters.toDate);
    if (filters.search) params.set('search', filters.search);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.fetchApi.fetch(
      `${baseUrl}/roles/audit/export${qs}`,
    );
    if (!response.ok) {
      throw new Error('Failed to export audit log');
    }
    return response.blob();
  }

  async invalidateRbacCache(
    userEntityRef?: string,
  ): Promise<{ invalidated: string }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/roles/cache/invalidate`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: userEntityRef ? JSON.stringify({ userEntityRef }) : '{}',
      },
    );
    if (!response.ok) {
      const msg = await this.extractError(
        response,
        'Failed to invalidate RBAC cache',
      );
      throw new Error(msg);
    }
    return response.json();
  }

  async getProjectPolicies(
    projectKey: string,
  ): Promise<Record<string, unknown>> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/roles/projects/${encodeURIComponent(projectKey)}/policies`,
    );
    if (!response.ok) return {};
    const result = await response.json();
    return result.data;
  }

  async setProjectPolicy(
    projectKey: string,
    policyKey: string,
    value: unknown,
  ): Promise<unknown> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/roles/projects/${encodeURIComponent(
        projectKey,
      )}/policies/${encodeURIComponent(policyKey)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value }),
      },
    );
    if (!response.ok) {
      const msg = await this.extractError(response, 'Failed to set policy');
      throw new Error(msg);
    }
    return response.json();
  }

  async getProjectCompliance(
    projectKey: string,
    days = 30,
  ): Promise<ComplianceMetrics> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/roles/projects/${encodeURIComponent(
        projectKey,
      )}/compliance?days=${days}`,
    );
    if (!response.ok) {
      await response.text().catch(() => '');
      return {
        projectKey,
        period: { days, since: new Date().toISOString() },
        activities: { total: 0, active: 0, completed: 0, errors: 0 },
        artifacts: { total: 0, approved: 0, pending: 0 },
        auditActions: 0,
        members: { total: 0, temporary: 0, expired: 0 },
        policies: {},
      };
    }
    return response.json();
  }

  async finalizeActivity(activityId: string): Promise<Activity> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/activities/${encodeURIComponent(activityId)}/finalize`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' } },
    );
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Failed to finalize activity');
    }
    return response.json();
  }

  // ─── Azure Boards — Work Items ───

  async listWorkItemTypes(projectId: string): Promise<AzureWorkItemType[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/azure/projects/${encodeURIComponent(
        projectId,
      )}/workitem-types`,
    );
    if (!response.ok) {
      const msg = await this.extractError(
        response,
        'Failed to list work item types',
      );
      throw new Error(msg);
    }
    return response.json();
  }

  async searchWorkItems(
    projectId: string,
    filters?: Omit<SearchWorkItemsFilters, 'projectId'>,
  ): Promise<PaginatedWorkItems> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();
    if (filters?.types?.length) params.set('types', filters.types.join(','));
    if (filters?.states?.length) params.set('states', filters.states.join(','));
    if (filters?.assignedToMe) params.set('assignedToMe', 'true');
    if (filters?.text) params.set('text', filters.text);
    if (filters?.workItemId)
      params.set('workItemId', String(filters.workItemId));
    if (filters?.areaPath) params.set('areaPath', filters.areaPath);
    if (filters?.iterationPath)
      params.set('iterationPath', filters.iterationPath);
    if (filters?.top) params.set('top', String(filters.top));
    if (filters?.skip) params.set('skip', String(filters.skip));

    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.fetchApi.fetch(
      `${baseUrl}/azure/projects/${encodeURIComponent(
        projectId,
      )}/workitems${qs}`,
    );
    if (!response.ok) {
      const msg = await this.extractError(
        response,
        'Failed to search work items',
      );
      throw new Error(msg);
    }
    return response.json();
  }

  async getWorkItem(
    projectId: string,
    workItemId: number,
  ): Promise<AzureWorkItemDetail> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/azure/projects/${encodeURIComponent(
        projectId,
      )}/workitems/${workItemId}`,
    );
    if (response.status === 404) {
      throw new Error('WORK_ITEM_NOT_FOUND');
    }
    if (response.status === 403) {
      throw new Error('AZURE_PERMISSION_DENIED');
    }
    if (!response.ok) {
      const msg = await this.extractError(response, 'Failed to get work item');
      throw new Error(msg);
    }
    return response.json();
  }

  private async processSSEStream(
    response: Response,
    onEvent: (event: StreamEvent) => void,
    streamOptions?: StreamOptions,
  ): Promise<void> {
    const hasOptions = streamOptions?.onHeartbeat || streamOptions?.onSeqId;
    return processSSEStream(
      response,
      hasOptions
        ? {
            onEvent,
            onHeartbeat: streamOptions?.onHeartbeat,
            onSeqId: streamOptions?.onSeqId,
          }
        : onEvent,
    );
  }
}

/** Surfaces the backend's deterministic public message instead of a generic one. */
async function deletionErrorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { message?: string };
    if (body?.message) return body.message;
  } catch {
    // Falls through to the generic message.
  }
  return 'A exclusão administrativa não pôde ser concluída.';
}
